import { useEffect, useMemo, useRef, useState } from 'react'

import { startCase } from 'lodash'
import { FiChevronDown, FiX } from 'react-icons/fi'

import { AlarmSeverityFilter } from '@/pages/assets/map/MapContext'
import { getSeverityColor, getSeverityFromId } from '@/pages/assets/map/utils'

function IndeterminateCheckbox({
  checked,
  indeterminate,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { indeterminate: boolean }) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate
    }
  }, [indeterminate])
  return <input type="checkbox" ref={ref} checked={checked} {...props} />
}

type FilteredAlarmCode = {
  id: number
  code: string
  description: string
  severity: 'critical' | 'warning' | 'informational'
  displayText: string
}

interface AlarmCodeFilterProps {
  alarmCodes: Array<{ id: number; code: string; description: string; severityId: number }>
  selectedCodeIds: number[]
  onChange: (codeIds: number[]) => void
  availableCodeIds?: number[]
  severityFilter?: AlarmSeverityFilter
  showSeverityCheckboxes?: boolean
  showNoAlarmsCheckbox?: boolean
  isOpen?: boolean
  onToggle?: () => void
  renderMode?: 'dropdown' | 'inline'
  onSeverityChange?: (severity: AlarmSeverityFilter) => void
  activeSince?: string | null
  onActiveSinceChange?: (value: string | null) => void
  isLoading?: boolean
}

interface DesktopFilterProps {
  alarmCodeSearch: string
  setAlarmCodeSearch: (value: string) => void
  renderMode: 'dropdown' | 'inline'
  showSeverityCheckboxes: boolean
  severityFilter?: AlarmSeverityFilter
  severityCheckStates: Record<string, 'checked' | 'indeterminate' | 'unchecked'>
  handleSeverityToggle: (severity: 'critical' | 'warning' | 'informational') => void
  showNoAlarmsCheckbox: boolean
  noAlarmsChecked: boolean
  onNoAlarmsChange: (checked: boolean) => void
  showSelectedAlarmCodes: boolean
  setShowSelectedAlarmCodes: (value: boolean) => void
  selectedCodeIds: number[]
  filteredAlarmCodes: FilteredAlarmCode[]
  availableAlarmCodes: FilteredAlarmCode[]
  handleAlarmCodeToggle: (alarmId: number, checked: boolean) => void
  handleClearSelected: () => void
  onChange: (codeIds: number[]) => void
  pendingActiveSince: string
  setPendingActiveSince: (value: string) => void
  onApplyActiveSince: () => void
  onClearActiveSince: () => void
  activeSince?: string | null
  onActiveSinceChange?: (value: string | null) => void
  isLoading?: boolean
}

interface MobileFilterProps {
  alarmCodeSearch: string
  setAlarmCodeSearch: (value: string) => void
  showSeverityCheckboxes: boolean
  severityFilter?: AlarmSeverityFilter
  severityCheckStates: Record<string, 'checked' | 'indeterminate' | 'unchecked'>
  handleSeverityToggle: (severity: 'critical' | 'warning' | 'informational') => void
  showNoAlarmsCheckbox: boolean
  noAlarmsChecked: boolean
  onNoAlarmsChange: (checked: boolean) => void
  showSelectedAlarmCodes: boolean
  setShowSelectedAlarmCodes: (value: boolean) => void
  selectedCodeIds: number[]
  filteredAlarmCodes: FilteredAlarmCode[]
  availableAlarmCodes: FilteredAlarmCode[]
  handleAlarmCodeToggle: (alarmId: number, checked: boolean) => void
  handleClearSelected: () => void
  onChange: (codeIds: number[]) => void
  pendingActiveSince: string
  setPendingActiveSince: (value: string) => void
  onApplyActiveSince: () => void
  onClearActiveSince: () => void
  activeSince?: string | null
  onActiveSinceChange?: (value: string | null) => void
  isLoading?: boolean
}

function DesktopFilterContent({
  alarmCodeSearch,
  setAlarmCodeSearch,
  renderMode,
  showSeverityCheckboxes,
  severityCheckStates,
  handleSeverityToggle,
  showNoAlarmsCheckbox,
  noAlarmsChecked,
  onNoAlarmsChange,
  showSelectedAlarmCodes,
  selectedCodeIds,
  filteredAlarmCodes,
  handleAlarmCodeToggle,
  setShowSelectedAlarmCodes,
  availableAlarmCodes,
  handleClearSelected,
  onChange,
  pendingActiveSince,
  setPendingActiveSince,
  onApplyActiveSince,
  onClearActiveSince,
  activeSince,
  onActiveSinceChange,
  isLoading,
}: DesktopFilterProps) {
  return (
    <>
      <div
        className={`bg-base-100 rounded-lg border border-base-300 ${
          renderMode === 'dropdown'
            ? 'absolute top-full left-0 mt-1 shadow-lg max-w-[400px] xl:min-w-[400px] xl:max-w-[500px] z-20'
            : 'w-full'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Severity checkboxes */}
        {showSeverityCheckboxes && (
          <div className="p-2 border-b border-base-300 flex items-center gap-4 whitespace-nowrap">
            {(['critical', 'warning', 'informational'] as const).map((severity) => {
              const state = severityCheckStates[severity] ?? 'unchecked'
              return (
                <label key={severity} className="flex items-center gap-1.5 cursor-pointer">
                  <IndeterminateCheckbox
                    checked={state === 'checked'}
                    indeterminate={state === 'indeterminate'}
                    onChange={() => handleSeverityToggle(severity)}
                    className="checkbox checkbox-xs rounded-none"
                  />
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: getSeverityColor(severity) }} />
                  <span className="text-xs">{startCase(severity)}</span>
                </label>
              )
            })}
            {showNoAlarmsCheckbox && (
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={noAlarmsChecked}
                  onChange={(e) => onNoAlarmsChange(e.target.checked)}
                  className="checkbox checkbox-xs rounded-none"
                />
                <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#6b7280' }} />
                <span className="text-xs">No Alarms</span>
              </label>
            )}
          </div>
        )}

        {/* Active Since date picker */}
        {onActiveSinceChange && (
          <div className="p-2 border-b border-base-300 flex items-center gap-2">
            <label className="text-xs whitespace-nowrap">Active Since</label>
            <input
              type="date"
              value={pendingActiveSince}
              onChange={(e) => setPendingActiveSince(e.target.value)}
              className="input input-bordered input-sm flex-1"
              max={new Date().toISOString().slice(0, 10)}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onApplyActiveSince()
              }}
              disabled={pendingActiveSince === (activeSince ?? '')}
              className="btn btn-xs btn-primary"
            >
              Apply
            </button>
            {activeSince && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onClearActiveSince()
                }}
                className="btn btn-ghost btn-xs btn-circle"
              >
                <FiX className="w-3 h-3" />
              </button>
            )}
          </div>
        )}

        {/* Search input */}
        <div className="p-2 border-b border-base-300">
          <div className="relative">
            <input
              type="text"
              placeholder="Search alarm codes..."
              value={alarmCodeSearch}
              onChange={(e) => setAlarmCodeSearch(e.target.value)}
              className="input input-bordered input-sm w-full pr-8"
              onClick={(e) => e.stopPropagation()}
            />
            {alarmCodeSearch && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setAlarmCodeSearch('')
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 btn btn-ghost btn-xs btn-circle"
                aria-label="Clear search"
              >
                <FiX className="w-4 h-4" />
              </button>
            )}
          </div>
          {/* Toggle and Clear buttons */}
          <div className="grid grid-cols-3 items-center mt-2">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                const allIds = availableAlarmCodes.map((a) => a.id)
                onChange(allIds)
              }}
              className="btn btn-xs btn-ghost justify-self-start"
            >
              Select All ({availableAlarmCodes.length})
            </button>
            {renderMode !== 'inline' ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setShowSelectedAlarmCodes(!showSelectedAlarmCodes)
                }}
                className={`btn btn-xs justify-self-center ${showSelectedAlarmCodes ? 'btn-primary' : 'btn-ghost'}`}
              >
                {showSelectedAlarmCodes
                  ? `Show All (${availableAlarmCodes.length})`
                  : `Show Selected (${selectedCodeIds.length})`}
              </button>
            ) : (
              <div />
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                handleClearSelected()
              }}
              disabled={selectedCodeIds.length === 0}
              className={`btn btn-xs btn-ghost justify-self-end ${selectedCodeIds.length === 0 ? 'text-gray-400' : ''}`}
            >
              Clear Selected
            </button>
          </div>
        </div>
        {/* Alarm codes list */}
        <div className="max-h-60 overflow-y-auto p-2">
          {isLoading ? (
            <div className="flex items-center justify-center p-4">
              <span className="loading loading-spinner loading-sm" />
              <span className="text-xs text-base-content/60 ml-2">Loading alarm codes...</span>
            </div>
          ) : showSelectedAlarmCodes && selectedCodeIds.length === 0 ? (
            <div className="text-xs text-base-content/60 p-2 text-center">None Selected</div>
          ) : filteredAlarmCodes.length === 0 ? (
            <div className="text-xs text-base-content/60 p-2 text-center">
              {alarmCodeSearch ? 'No alarm codes found' : 'No alarm codes available'}
            </div>
          ) : (
            <div className="space-y-1">
              {filteredAlarmCodes.map((alarm) => (
                <label key={alarm.id} className="flex items-center gap-2 cursor-pointer hover:bg-base-200 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={selectedCodeIds.includes(alarm.id)}
                    onChange={(e) => handleAlarmCodeToggle(alarm.id, e.target.checked)}
                    className="checkbox checkbox-sm rounded-none"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="text-xs flex-1">{alarm.displayText || `${alarm.description} (${alarm.code})`}</span>
                  <div
                    className="w-3 h-3 rounded-sm border border-base-300"
                    style={{ backgroundColor: getSeverityColor(alarm.severity) }}
                    title={alarm.severity}
                  />
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

function MobileFilterContent({
  alarmCodeSearch,
  setAlarmCodeSearch,
  showSeverityCheckboxes,
  severityCheckStates,
  handleSeverityToggle,
  showNoAlarmsCheckbox,
  noAlarmsChecked,
  onNoAlarmsChange,
  showSelectedAlarmCodes,
  selectedCodeIds,
  filteredAlarmCodes,
  handleAlarmCodeToggle,
  setShowSelectedAlarmCodes,
  availableAlarmCodes,
  handleClearSelected,
  onChange,
  pendingActiveSince,
  setPendingActiveSince,
  onApplyActiveSince,
  onClearActiveSince,
  activeSince,
  onActiveSinceChange,
  isLoading,
}: MobileFilterProps) {
  return (
    <div>
      {/* Severity checkboxes */}
      <div>
        <div className="text-sm text-base-content/60 mb-4">Select by severity</div>
        {showSeverityCheckboxes && (
          <div className="flex flex-wrap gap-4 mb-4">
            {(['critical', 'warning', 'informational'] as const).map((severity) => {
              const state = severityCheckStates[severity] ?? 'unchecked'
              return (
                <label
                  key={severity}
                  className="flex items-center gap-1.5 cursor-pointer py-2 px-4 rounded-full bg-base-200 has-checked:bg-black/70 has-checked:text-white"
                >
                  <IndeterminateCheckbox
                    checked={state === 'checked'}
                    indeterminate={state === 'indeterminate'}
                    onChange={() => handleSeverityToggle(severity)}
                    className="checkbox checkbox-xs rounded-none peer hidden"
                  />
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: getSeverityColor(severity) }} />
                  <span className="text-xs">{startCase(severity)}</span>
                </label>
              )
            })}
            {showNoAlarmsCheckbox && (
              <label className="flex items-center gap-1.5 cursor-pointer py-2 px-4 rounded-full bg-base-200 has-checked:bg-black/70 has-checked:text-white">
                <input
                  type="checkbox"
                  checked={noAlarmsChecked}
                  onChange={(e) => onNoAlarmsChange(e.target.checked)}
                  className="checkbox checkbox-xs rounded-none peer hidden"
                />
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#6b7280' }} />
                <span className="text-xs">No Alarms</span>
              </label>
            )}
          </div>
        )}
      </div>
      {/* Active Since date picker */}
      {onActiveSinceChange && (
        <div className="p-3 border-b border-base-300 flex items-center gap-2">
          <label className="text-sm whitespace-nowrap">Active Since</label>
          <input
            type="date"
            value={pendingActiveSince}
            onChange={(e) => setPendingActiveSince(e.target.value)}
            className="input input-bordered flex-1"
            max={new Date().toISOString().slice(0, 10)}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onApplyActiveSince()
            }}
            disabled={pendingActiveSince === (activeSince ?? '')}
            className="btn btn-sm btn-primary"
          >
            Apply
          </button>
          {activeSince && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onClearActiveSince()
              }}
              className="btn btn-ghost btn-xs btn-circle"
            >
              <FiX className="w-3 h-3" />
            </button>
          )}
        </div>
      )}
      {/* Search input */}
      <div className="p-3 border-b border-base-300">
        <div className="relative">
          <input
            type="text"
            placeholder="Search alarm codes..."
            value={alarmCodeSearch}
            onChange={(e) => setAlarmCodeSearch(e.target.value)}
            className="input input-bordered w-full"
            onClick={(e) => e.stopPropagation()}
          />
          {alarmCodeSearch && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setAlarmCodeSearch('')
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 btn btn-ghost btn-xs btn-circle"
              aria-label="Clear search"
            >
              <FiX className="w-4 h-4" />
            </button>
          )}
        </div>
        {/* Toggle and Clear buttons */}
        <div className="grid grid-cols-3 items-center mt-4">
          <label className="label justify-self-start">
            <input
              type="checkbox"
              className="toggle"
              checked={selectedCodeIds.length === availableAlarmCodes.length} // ✅
              onChange={(e) => {
                e.stopPropagation()
                if (e.target.checked) {
                  const allIds = availableAlarmCodes.map((a) => a.id)
                  onChange(allIds)
                } else {
                  onChange([])
                }
              }}
            />
            <span> Select All ({availableAlarmCodes.length})</span>
          </label>
          <label className="label justify-self-center">
            <input
              type="checkbox"
              className="toggle"
              checked={showSelectedAlarmCodes}
              onChange={(e) => {
                e.stopPropagation()
                setShowSelectedAlarmCodes(e.target.checked)
              }}
            />
            <span>
              {' '}
              {showSelectedAlarmCodes
                ? `Show All (${availableAlarmCodes.length})`
                : `Show Selected (${selectedCodeIds.length})`}
            </span>
          </label>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              handleClearSelected()
            }}
            disabled={selectedCodeIds.length === 0}
            className={`btn btn-xs btn-ghost justify-self-end ${selectedCodeIds.length === 0 ? 'text-gray-400' : ''}`}
          >
            Clear Selected
          </button>
        </div>
      </div>
      {/* Alarm codes list */}
      <div className="max-h-72 overflow-y-auto p-2 ">
        {isLoading ? (
          <div className="flex items-center justify-center p-4">
            <span className="loading loading-spinner loading-sm" />
            <span className="text-xs text-base-content/60 ml-2">Loading alarm codes...</span>
          </div>
        ) : showSelectedAlarmCodes && selectedCodeIds.length === 0 ? (
          <div className="text-xs text-base-content/60 p-2 text-center">None Selected</div>
        ) : filteredAlarmCodes.length === 0 ? (
          <div className="text-xs text-base-content/60 p-2 text-center">
            {alarmCodeSearch ? 'No alarm codes found' : 'No alarm codes available'}
          </div>
        ) : (
          <div className="space-y-1">
            {filteredAlarmCodes.map((alarm) => (
              <label
                key={alarm.id}
                className="flex items-center gap-2 cursor-pointer hover:bg-base-200 px-1 py-2 active:bg-base-200"
              >
                <input
                  type="checkbox"
                  checked={selectedCodeIds.includes(alarm.id)}
                  onChange={(e) => handleAlarmCodeToggle(alarm.id, e.target.checked)}
                  className="checkbox checkbox-sm rounded-none"
                  onClick={(e) => e.stopPropagation()}
                />
                <span className="text-xs flex-1">{alarm.displayText || `${alarm.description} (${alarm.code})`}</span>
                <div
                  className="w-3 h-3 rounded-sm border border-base-300"
                  style={{ backgroundColor: getSeverityColor(alarm.severity) }}
                  title={alarm.severity}
                />
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default function AlarmCodeFilter({
  alarmCodes,
  selectedCodeIds,
  onChange,
  availableCodeIds,
  severityFilter,
  showSeverityCheckboxes = false,
  showNoAlarmsCheckbox = false,
  renderMode = 'inline',
  isOpen,
  onToggle,
  onSeverityChange,
  activeSince,
  onActiveSinceChange,
  isLoading,
}: AlarmCodeFilterProps) {
  const [alarmCodeSearch, setAlarmCodeSearch] = useState('')
  const [showSelectedAlarmCodes, setShowSelectedAlarmCodes] = useState(false)
  const [pendingActiveSince, setPendingActiveSince] = useState(activeSince ?? '')

  useEffect(() => {
    setPendingActiveSince(activeSince ?? '')
  }, [activeSince])

  const availableCodesSet = useMemo(() => {
    return availableCodeIds ? new Set(availableCodeIds) : null
  }, [availableCodeIds])

  const availableAlarmCodes = useMemo(() => {
    return alarmCodes
      .filter((alarmCode) => {
        if (availableCodesSet && !availableCodesSet.has(alarmCode.id)) {
          return false
        }
        return true
      })
      .map((alarmCode) => ({
        id: alarmCode.id,
        code: alarmCode.code.toString(),
        description: alarmCode.description,
        severity: getSeverityFromId(alarmCode.severityId),
        displayText: `${alarmCode.description} (${alarmCode.code})`,
      }))
      .sort((a, b) => a.displayText.localeCompare(b.displayText))
  }, [alarmCodes, availableCodesSet])

  // Compute severity checkbox states: checked (all selected), indeterminate (some), unchecked (none)
  const severityCheckStates = useMemo(() => {
    const states: Record<string, 'checked' | 'indeterminate' | 'unchecked'> = {}
    const selectedSet = new Set(selectedCodeIds)
    for (const sev of ['critical', 'warning', 'informational'] as const) {
      const codesForSeverity = availableAlarmCodes.filter((c) => c.severity === sev)
      if (codesForSeverity.length === 0) {
        states[sev] = 'unchecked'
        continue
      }
      const selectedCount = codesForSeverity.filter((c) => selectedSet.has(c.id)).length
      if (selectedCount === 0) states[sev] = 'unchecked'
      else if (selectedCount === codesForSeverity.length) states[sev] = 'checked'
      else states[sev] = 'indeterminate'
    }
    return states
  }, [availableAlarmCodes, selectedCodeIds])

  // When a severity checkbox is toggled, select or deselect all codes of that severity
  const handleSeverityToggle = (severity: 'critical' | 'warning' | 'informational') => {
    const codesForSeverity = availableAlarmCodes.filter((c) => c.severity === severity)
    const selectedSet = new Set(selectedCodeIds)
    const allSelected = codesForSeverity.every((c) => selectedSet.has(c.id))

    if (allSelected) {
      // Deselect all codes of this severity
      codesForSeverity.forEach((c) => selectedSet.delete(c.id))
    } else {
      // Select all codes of this severity
      codesForSeverity.forEach((c) => selectedSet.add(c.id))
    }
    onChange(Array.from(selectedSet))
  }

  const filteredAlarmCodes = useMemo(() => {
    let codes = availableAlarmCodes

    if (showSelectedAlarmCodes && selectedCodeIds.length > 0) {
      const selectedSet = new Set(selectedCodeIds)
      codes = codes.filter((alarm) => selectedSet.has(alarm.id))
    }

    if (alarmCodeSearch) {
      const searchLower = alarmCodeSearch.toLowerCase()
      codes = codes.filter(
        (alarm) =>
          String(alarm.code).toLowerCase().includes(searchLower) ||
          alarm.description.toLowerCase().includes(searchLower)
      )
    }
    return codes
  }, [availableAlarmCodes, alarmCodeSearch, showSelectedAlarmCodes, selectedCodeIds])

  const handleAlarmCodeToggle = (alarmId: number, checked: boolean) => {
    const newSet = new Set(selectedCodeIds)
    if (checked) {
      newSet.add(alarmId)
    } else {
      newSet.delete(alarmId)
    }
    onChange(Array.from(newSet)) // Convert Set back to array for onChange
  }

  const handleClearSelected = () => {
    onChange([])
    setShowSelectedAlarmCodes(false)
  }

  const handleApplyActiveSince = () => {
    onActiveSinceChange?.(pendingActiveSince || null)
  }

  const handleClearActiveSince = () => {
    setPendingActiveSince('')
    onActiveSinceChange?.(null)
  }

  const handleNoAlarmsChange = (checked: boolean) => {
    onSeverityChange?.({
      critical: severityFilter?.critical ?? true,
      warning: severityFilter?.warning ?? true,
      informational: severityFilter?.informational ?? true,
      noAlarms: checked,
    })
  }

  // Select all codes by default when codes are first loaded and nothing is selected
  const hasInitialized = useRef(false)
  useEffect(() => {
    if (hasInitialized.current) return
    if (availableAlarmCodes.length === 0) return
    hasInitialized.current = true
    if (selectedCodeIds.length === 0) {
      onChange(availableAlarmCodes.map((c) => c.id))
    }
  }, [availableAlarmCodes, selectedCodeIds, onChange])

  return (
    <>
      {renderMode === 'dropdown' ? (
        <>
          {/* Desktop dropdown version */}
          <div className="relative hidden md:block" data-filter-dropdown>
            <button
              onClick={(e) => {
                e.stopPropagation()
                onToggle?.()
              }}
              className="btn btn-sm btn-outline min-w-[160px] flex items-center justify-between [&::before]:hidden
  [&::after]:hidden font-normal"
            >
              <span>
                Active Alarms
                {selectedCodeIds.length > 0 && <span className="ml-2 text-primary">({selectedCodeIds.length})</span>}
              </span>
              <FiChevronDown
                className={`w-4 h-4 text-base-content/60 transition-transform duration-200 ml-auto ${
                  isOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
            {isOpen && (
              <div
                className="absolute top-full left-0 mt-2 w-80 bg-base-100 rounded-lg shadow-xl border border-base-300 
  z-50"
              >
                <DesktopFilterContent
                  alarmCodeSearch={alarmCodeSearch}
                  setAlarmCodeSearch={setAlarmCodeSearch}
                  renderMode={renderMode}
                  showSeverityCheckboxes={showSeverityCheckboxes}
                  severityFilter={severityFilter}
                  severityCheckStates={severityCheckStates}
                  handleSeverityToggle={handleSeverityToggle}
                  showNoAlarmsCheckbox={showNoAlarmsCheckbox}
                  noAlarmsChecked={severityFilter?.noAlarms ?? true}
                  onNoAlarmsChange={handleNoAlarmsChange}
                  showSelectedAlarmCodes={showSelectedAlarmCodes}
                  setShowSelectedAlarmCodes={setShowSelectedAlarmCodes}
                  selectedCodeIds={selectedCodeIds}
                  filteredAlarmCodes={filteredAlarmCodes}
                  availableAlarmCodes={availableAlarmCodes}
                  handleAlarmCodeToggle={handleAlarmCodeToggle}
                  handleClearSelected={handleClearSelected}
                  onChange={onChange}
                  pendingActiveSince={pendingActiveSince}
                  setPendingActiveSince={setPendingActiveSince}
                  onApplyActiveSince={handleApplyActiveSince}
                  onClearActiveSince={handleClearActiveSince}
                  activeSince={activeSince}
                  onActiveSinceChange={onActiveSinceChange}
                  isLoading={isLoading}
                />
              </div>
            )}
          </div>

          {/* Mobile collapse version */}
          <div className="md:hidden w-full">
            <details className="collapse collapse-arrow" name="alarm-filter-det">
              <summary className="collapse-title text-xl">
                Active Alarms
                {selectedCodeIds.length > 0 && ` (${selectedCodeIds.length})`}
              </summary>
              <div className="collapse-content">
                <MobileFilterContent
                  alarmCodeSearch={alarmCodeSearch}
                  setAlarmCodeSearch={setAlarmCodeSearch}
                  showSeverityCheckboxes={showSeverityCheckboxes}
                  severityFilter={severityFilter}
                  severityCheckStates={severityCheckStates}
                  handleSeverityToggle={handleSeverityToggle}
                  showNoAlarmsCheckbox={showNoAlarmsCheckbox}
                  noAlarmsChecked={severityFilter?.noAlarms ?? true}
                  onNoAlarmsChange={handleNoAlarmsChange}
                  showSelectedAlarmCodes={showSelectedAlarmCodes}
                  setShowSelectedAlarmCodes={setShowSelectedAlarmCodes}
                  selectedCodeIds={selectedCodeIds}
                  filteredAlarmCodes={filteredAlarmCodes}
                  availableAlarmCodes={availableAlarmCodes}
                  handleAlarmCodeToggle={handleAlarmCodeToggle}
                  handleClearSelected={handleClearSelected}
                  onChange={onChange}
                  pendingActiveSince={pendingActiveSince}
                  setPendingActiveSince={setPendingActiveSince}
                  onApplyActiveSince={handleApplyActiveSince}
                  onClearActiveSince={handleClearActiveSince}
                  activeSince={activeSince}
                  onActiveSinceChange={onActiveSinceChange}
                  isLoading={isLoading}
                />
              </div>
            </details>
          </div>
        </>
      ) : (
        // Inline mode for ConfigControls - just render the content, no button wrapper
        <div className="w-full">
          <DesktopFilterContent
            alarmCodeSearch={alarmCodeSearch}
            setAlarmCodeSearch={setAlarmCodeSearch}
            renderMode={renderMode}
            showSeverityCheckboxes={showSeverityCheckboxes}
            severityFilter={severityFilter}
            severityCheckStates={severityCheckStates}
            handleSeverityToggle={handleSeverityToggle}
            showNoAlarmsCheckbox={showNoAlarmsCheckbox}
            noAlarmsChecked={severityFilter?.noAlarms ?? true}
            onNoAlarmsChange={handleNoAlarmsChange}
            showSelectedAlarmCodes={showSelectedAlarmCodes}
            setShowSelectedAlarmCodes={setShowSelectedAlarmCodes}
            selectedCodeIds={selectedCodeIds}
            filteredAlarmCodes={filteredAlarmCodes}
            availableAlarmCodes={availableAlarmCodes}
            handleAlarmCodeToggle={handleAlarmCodeToggle}
            handleClearSelected={handleClearSelected}
            onChange={onChange}
            pendingActiveSince={pendingActiveSince}
            setPendingActiveSince={setPendingActiveSince}
            onApplyActiveSince={handleApplyActiveSince}
            onClearActiveSince={handleClearActiveSince}
            activeSince={activeSince}
            onActiveSinceChange={onActiveSinceChange}
            isLoading={isLoading}
          />
        </div>
      )}
    </>
  )
}
